import { brandPopupHandler } from "../../global";
import { cachedAPIWrapper } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";
import { startLoader, stopLoader } from "../util/loader";
import { fetchSearchTerms } from "./helperCalls";
import { validateLocalZipPostalCode, isValidCAPostalCode } from "../nbly-brand/helper_functions";
import { isValidZipCode } from "../apiCaching/POST-Requests/helperModules/helperFunctions";
import { getCookie } from "../util/share";
import { updateAggregateMarkup } from "./search-results-page";
import { getPageSettings } from "../util/pageSettings";

// Extend the Window interface to include initFlowbite
declare global {
    interface Window {
        initFlowbite?: () => void;
    }
}

type ServiceItem = {
    searchTerm: string;
    [key: string]: any;
};



export class BrandResult {
    private static instance: BrandResult;
    static getInstance(): BrandResult {
        if (!BrandResult.instance) {
            BrandResult.instance = new BrandResult();
        }
        return BrandResult.instance;
    }

    private readonly searchZipCode: HTMLInputElement | null;
    private readonly searchInput: HTMLInputElement | null;
    private fallbackExecuted: boolean = false;
    private brandListListenerAttached: boolean = false;
    private constructor() {
        this.searchZipCode = document.querySelector('.search-result-hero-banner .zip-input') as HTMLInputElement;
        this.searchInput = document.querySelector('.search-result-banner-wrap .service-input') as HTMLInputElement;
        
        // Prepopulate query string on page load
        this.populateFromQueryString();
        
        document.querySelector('.search-result-banner-wrap .search-btn.primary-btn')?.addEventListener("click", (e) => {
            this.handleSearchClick(e);
        });
        this.searchZipCode?.addEventListener('input', () => {
            this.validZipCode();
        });
        this.searchInput?.addEventListener('change', () => this.removeServiceIdOnChange());
        this.attachEventListenerToCtaButton();
        this.handleEmptySearchResult();
    }
    
    attachEventListenerToCtaButton(){
        if (this.brandListListenerAttached) {
            return;
        }
        const brandList = document.getElementById('brand-list');
        if (brandList) {
            brandList.addEventListener("click", (e) => {
                this.clickInterceptorForCampaignNames(e);
            });
            this.brandListListenerAttached = true;
        }else{
            this.handleNoResults( this.searchInput?.value ?? "");
        }
    }

    removeServiceIdOnChange(){
        if(this.searchInput){
            delete this.searchInput.dataset.serviceId;
        }
    }

    private populateFromQueryString(): void {
        const urlParams = new URLSearchParams(globalThis.location.search);
        const serviceId = urlParams.get('service');
        const address = urlParams.get('address');
        const name = urlParams.get('name');
        
        // Populate service input with name and service ID
        if (this.searchInput && name) {
            this.searchInput.value = decodeURIComponent(name);
            
            if (serviceId) {
                this.searchInput.dataset.serviceId = serviceId;
            }
        }
        
        // Populate zip code input with address
        if (this.searchZipCode && address) {
            this.searchZipCode.value = decodeURIComponent(address);
            sessionStorage.setItem('brandZipCode', this.searchZipCode.value);
        }
    }

    private validZipCode() {
        // Validate the ZIP code using the regex pattern
        const zipCode = this.searchZipCode?.value?.trim() ?? '';
        const isZipInvalid = zipCode !== '' && !validateLocalZipPostalCode(zipCode);
         this.toggleErrorVisibility('zip-error-msg', isZipInvalid);
    }

    private toggleErrorVisibility(errorId: string, isVisible: boolean) {
        const fieldError = document.getElementById(errorId);
        if (fieldError) {
            if (isVisible) {
                fieldError.classList.remove('hidden');
            } else {
                fieldError.classList.add('hidden');
            }
        }
    }


    public async handleSearchClick(event: Event) {
        event.preventDefault();
        this.fallbackExecuted = false;
        const zipCode = this.searchZipCode?.value?.trim();
        const legacyZipCheck = isValidZipCode(zipCode);
        const isZipValid = zipCode !== '' && validateLocalZipPostalCode(zipCode);
        const zipFail = isZipValid && zipCode && legacyZipCheck && legacyZipCheck !== '00000';
        
        if (this.validateSearchInput() && zipFail) {
            if (isValidCAPostalCode(zipCode)) {
                const serviceId = this.searchInput?.dataset?.serviceId || '';
                const serviceName = this.searchInput?.value?.trim() || '';
                const searchPath = '/ca/search-result';
                const searchUrl = `${window.location.origin}${searchPath}?service=${serviceId}&address=${encodeURIComponent(zipCode)}&name=${encodeURIComponent(serviceName)}`;
                window.location.replace(searchUrl);
            } else {
                await this.performBrandSearch();
            }
        }
        else {
            // Show error message if ZIP code is invalid

            this.updateErrorVisibility(zipFail ? true : false);
        }
    }

    

    findBySearchTerm(items: ServiceItem[], str: string | undefined): ServiceItem | undefined {
        if (!str) return undefined;

        const search = str.trim().toLowerCase();

        return items.find(
            item => item.searchTerm?.toLowerCase() === search
        );
    }
    
    async handleFallbackSearchCondition(serviceName: string, zipCode: string){
        const searchTerms = await fetchSearchTerms(serviceName, zipCode);
        if(searchTerms.length > 0){
            const result = this.findBySearchTerm(searchTerms, serviceName);
            if(result){
                this.performBrandSearch(result.searchTerm, zipCode, result.serviceId);
            }
            else{
                this.handleNoResults(serviceName);
            }
        }
        else{
            this.handleNoResults(serviceName);
        }
    }

    handleEmptySearchResult(){
        setTimeout(() => {
            const noResultContainerSpan = document.querySelector('.no-search-results .nbly-search-title span');
            const noResultContainer = document.querySelector('.no-search-results');
            const searchResultContainer = document.querySelector('.nbly-search-results');

            const section = document.querySelector('.new-search-result-section') as HTMLElement;
            const newSearchResultsList = document.querySelectorAll('.new-search-results li');

            if(noResultContainerSpan?.textContent === '""'){
                if(noResultContainer && searchResultContainer){
                    noResultContainer.classList.add('hidden');
                }
            }
            else{
                if(noResultContainer && searchResultContainer && section && newSearchResultsList?.length === 0){
                    searchResultContainer.classList.remove('hidden');
                }
            }
        },0)
        
    }

    handleNoResults(serviceName: string | undefined) {
        const section = document.querySelector('.new-search-result-section') as HTMLElement;
        const newSearchResultsList = document.querySelectorAll('.new-search-results li');

        const noResultContainer = document.querySelector('.no-search-results');
        const searchResultContainer = document.querySelector('.nbly-search-results');
        if(searchResultContainer){
            searchResultContainer.classList.remove('hidden');
        }
        if(noResultContainer && section && newSearchResultsList?.length === 0) {
            noResultContainer.classList.remove('hidden');
            section.style.display = 'none';
            const searchText = noResultContainer.querySelector('.nbly-search-title span');
            if(searchText){
                searchText.textContent = `"${serviceName || ''}"`;
            }
        }
        this.showBottomServices();
        this.hideSearchResults();
        
    }

    hideNoResultsContainer(){
        const noResultContainer = document.querySelector('.no-search-results');
        if(noResultContainer){
            noResultContainer.classList.add('hidden');
        }
    }


    public async performBrandSearch(serviceValue?: string, zipValue?: string, serviceIdValue?: string): Promise<void> {
        const serviceName = serviceValue ?? this.searchInput?.value;
        const zipCode = zipValue ?? this.searchZipCode?.value;
        const serviceId = serviceIdValue || this.searchInput?.dataset?.serviceId;
        const section = document.querySelector('.new-search-result-section') as HTMLElement;
        const noResultContainer = document.querySelector('.no-search-results');

         if(section){
            const newSearchResultsList = document.querySelectorAll('.new-search-results li');
            const parentContainer = document.querySelector('.nbly-search-results .container');
            if(newSearchResultsList?.length > 0 && parentContainer){
                parentContainer.classList.add('hidden');
            }
         }
        
        startLoader();
        try {
            const isCA = isValidCAPostalCode(zipCode || '');
            const searchEndpoint = isCA ? `${window.location.origin}/ca/searchservice` : apiConfig.BRAND_SEARCH_API;
       
            const pageTemplate = getPageSettings('pagetemplate');
            if (pageTemplate?.toLowerCase().includes('opus 2 neighborly search results')){
                this.hideBottomServices();
            };

            const response = await cachedAPIWrapper(`${searchEndpoint}/${serviceName}/${zipCode}/${serviceId ? serviceId : ''}`, 60);
            if (response) {
                // to add that logic here to show/hide no results container for nei

                if (response && response?.Source === 'search-results') {
                    const brandResult = BrandResult.getInstance();
                    brandResult.updateMarkup(response);
                    section.style.display = 'none';
                    this.hideNoResultsContainer();
                }
                else if (response && response?.Source === 'aggregate-results') {
                    console.log('aggregate-results', response);
                    section.style.display = 'block';
                    updateAggregateMarkup(response);
                    this.hideSearchResults();
                    this.hideNoResultsContainer();
                }
                else if (response && response?.Source === 'no-results') {
                    noResultContainer?.classList.remove('hidden');
                    section.style.display = 'none';
                    this.hideSearchResults();
                    this.showBottomServices();
                }


                if(zipCode)
                sessionStorage.setItem('brandZipCode',zipCode);
            }
            stopLoader();
        }
        catch (error) {
            console.warn(`Error while searching respective brands. Error message: ${error}`);
            const container = document.querySelector('#brand-list');
            if (container) {
                container.innerHTML = '';
            }
            stopLoader();
        }
    }

    private updateErrorVisibility(isZipValid: boolean) {
        if (!isZipValid) {
                const fieldError = document.getElementById('zip-error-msg');
                if (fieldError) {
                    fieldError.classList.remove('hidden');
                }
            }

            // Show error message if service input is invalid
            if (!this.validateSearchInput()) {
                const searchInputError = document.getElementById('service-error-msg');
                if (searchInputError) {
                    searchInputError.classList.remove('hidden');
                }
            }

    }

    public updateMarkup(data: any) {

        const serviceHighlight = document.querySelector('.service-highlight');
        if (serviceHighlight) {
            serviceHighlight.textContent = data.SearchTerm;  // Replace with the actual service term
        }
        const parentContainer = document.querySelector('.nbly-search-results .container');
        if (parentContainer) {
            // Remove existing brand-list and modal if they exist
            const existingBrandList = parentContainer.querySelector('#brand-list');
            if(existingBrandList){
                existingBrandList.remove();
            }
            // Check if SearchServiceResultMarkup is empty or contains only HTML comments/whitespace
            const markup = data.SearchServiceResultMarkup || '';
            if(markup === "<!-- Service Search FTE Template Start --><!-- Service Search FTE Template Start -->"){
                this.handleNoResults( this.searchInput?.value ?? "");
            }
            const cleanedMarkup = markup.replace(/<!--[\s\S]*?-->/g, '').trim();
            
            // Insert the SearchServiceResultMarkup into the container only if it has actual content
            if (cleanedMarkup) {
                const searchResultContainer = document.querySelector('.nbly-search-results');
                if(searchResultContainer){
                    searchResultContainer.classList.remove('hidden');
                }
                this.brandListListenerAttached = false;
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = markup;
                while (tempDiv.firstChild) {
                    parentContainer.appendChild(tempDiv.firstChild);
                }
                if (typeof window.initFlowbite === 'function') {
                    window.initFlowbite();
                    brandPopupHandler();
                }
                parentContainer.classList.remove('hidden');
                this.hideNoResultsContainer();
                this.hideBottomServices();
                this.attachEventListenerToCtaButton();
            }
            stopLoader();
        }
    }

    /**
     * Constructs and adds UTM tracking parameters to all CTA buttons with class 'visit-website'.
     * Appends utm_source, utm_medium, utm_campaign, and nbly_ga_id parameters to href URLs.
     * 
     * @throws {Error} If URL construction fails for any CTA button
     * @returns {void}
     * 
     * @security Validates URLs before modification to prevent malicious redirects
     * @performance Uses URLSearchParams for efficient parameter handling
     */
    constructAndAddUTMTagsToCTA(): void {
        // Externalize to config
        const UTM_CONFIG = {
            source: 'neighborly.com',
            medium: 'referral',
            campaign: 'neighborly_referral'
        };

        let gaId = getCookie('_ga');
        
        // Sanitize GA ID to prevent XSS
        const sanitizedGaId = gaId.replace(/[<>"']/g, '');

        const allCtaButtons = document.querySelectorAll<HTMLAnchorElement>('.visit-website');
        console.log(allCtaButtons);
        
        allCtaButtons.forEach((ctaButton) => {
            const href = ctaButton.getAttribute('href');
            
            // Early return with validation
            if (!href || typeof href !== 'string' || href.trim() === '') {
                return;
            }

            try {
                const url = new URL(href);
                if (!url.protocol.startsWith('http')) {
                    console.warn(`Skipping non-HTTP URL: ${href}`);
                    return;
                }

                url.searchParams.set('utm_source', UTM_CONFIG.source);
                url.searchParams.set('utm_medium', UTM_CONFIG.medium);
                url.searchParams.set('utm_campaign', UTM_CONFIG.campaign);
                if (sanitizedGaId) {
                    url.searchParams.set('nbly_ga_id', sanitizedGaId);
                }

                ctaButton.setAttribute('href', url.toString());
            } catch (error) {
                // Log error but don't break other CTAs
                console.error(`Failed to add UTM tags to CTA: ${href}`, error);
            }
        });
    }

    hideSearchResults(){
        const parentContainer = document.querySelector('.nbly-search-results .container');
        if(parentContainer){
            parentContainer.classList.add('hidden');
        }
    }

    hideBottomServices(){
        const popularServices = document.querySelector('.popular-service');
        if(popularServices){
            popularServices.classList.add('hidden');
        }
        const servicesList = document.querySelector('.services-list');
        if(servicesList){
            servicesList.classList.add('hidden');
        }
    }

    showBottomServices(){
        const popularServices = document.querySelector('.popular-service');
        if(popularServices){
            popularServices.classList.remove('hidden');
        }
        const servicesList = document.querySelector('.services-list');
        if(servicesList){
            servicesList.classList.remove('hidden');
        }
    }

    public validateSearchInput() {
        const serviceInput = document.querySelector('.search-result-banner-wrap .service-input');
        if (serviceInput instanceof HTMLInputElement) {
            return serviceInput?.value.trim() !== '';
        }
        return false;
    }

    clickInterceptorForCampaignNames(e: Event){
        const clickedElement = e.target as HTMLElement;
        const targetElement = clickedElement.closest('.booking-redirect') as HTMLAnchorElement;
        
        if(targetElement){
            const href = targetElement.getAttribute('href');
            if(href){
                e.preventDefault();
                e.stopPropagation();
                const campaignName = sessionStorage.getItem("campaignName");
                const extractedCampaign = campaignName?.split("?")[1];
                if(extractedCampaign){
                    const url = new URL(href, window.location.origin);
                    const campaignParams = new URLSearchParams(extractedCampaign);
                    campaignParams.forEach((value, key) => {
                        if (key === 'leadconcept' || key === 'weblocation') {
                            return;
                        }
                        url.searchParams.set(key, value);
                    });
                    targetElement.setAttribute('href', url.toString());
                }
                window.location.href = targetElement.getAttribute('href') || href;
            }
        }
    }
}

document.addEventListener('DOMContentLoaded', async () => {
    const searchResultForm = document.querySelector(".search-result-hero-banner") as HTMLElement;
    if (!searchResultForm) { return };
    if ((globalThis as any).__SearchResultFormInitialized) return;
    (globalThis as any).__SearchResultFormInitialized = true;
    BrandResult.getInstance();
    const section = document.querySelector('.new-search-result-section') as HTMLElement;
    const newSearchResultsList = document.querySelectorAll('.new-search-results li');
    const parentContainer = document.querySelector('.nbly-search-results .container');
    
    if(section && newSearchResultsList?.length > 0 && parentContainer){
        parentContainer.classList.add('hidden');
    }
     
});
